import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Box, Grid, Paper, Tabs, Tab, Button, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import moment from 'moment';
import { getReaderList, getDailyReaderHistoryStats, getHistoryMonthlyReaderStats } from '../services/readerService';
import {
  getRouteInfo,
  getHistoryRouteStats,
  getHistoryMonthlyHourlyRouteStats
} from '../services/routeService';
import { getCctvListNewTaipei2, getHistoryMonthlyDetectionStats } from '../services/cctvService';

import DailyReaderTrafficChart from '../components/DailyReaderTrafficChart';
import DailyRouteStatsChart from '../components/DailyRouteStatsChart';
import DailyVehicleDetectionChart from '../components/DailyVehicleDetectionChart';

// 歷史資料可查詢最早到 2020-01-01
const HISTORY_MIN_DATE = new Date('2020-01-01');

const HistoryDashboard = () => {
  const { teams } = useSelector((state) => state.teamsInfo);
  const { users } = useSelector((state) => state.userLogin);
  const index = teams.findIndex((team) => team.id === users.team_id);
  const teamName = teams[index]?.name;

  const [tabValue, setTabValue] = useState(0);
  const [readers, setReaders] = useState([]);
  const [routeInfo, setRouteInfo] = useState([]);
  const [selectroute, setselectroute] = useState('');

  const [downloadReaderMac, setDownloadReaderMac] = useState('');
  const [downloadReaderName, setDownloadReaderName] = useState('');

  // newtaipei2 專用狀態
  const [nt2Cameras, setNt2Cameras] = useState([]);
  const [nt2SelectedCamera, setNt2SelectedCamera] = useState('');
  const [nt2Date, setNt2Date] = useState(
    moment().subtract(1, 'days').format('YYYY-MM-DD')
  );

  const [readerDate, setReaderDate] = useState(
    moment().subtract(91, 'days').format('YYYY-MM-DD')
  );
  const [routeDate, setRouteDate] = useState(
    moment().subtract(91, 'days').format('YYYY-MM-DD')
  );

  useEffect(() => {
    if (routeInfo.length > 0 && !selectroute) {
      setselectroute(routeInfo[0].id);
    }
  }, [routeInfo]);

  useEffect(() => {
    if (readers.length > 0 && !downloadReaderMac) {
      setDownloadReaderMac(readers[0].leonReaderMACAddress);
      setDownloadReaderName(readers[0].name || readers[0].leonReaderMACAddress);
    }
  }, [readers]);

  const handleReaderDateChange = (newDate) => {
    setReaderDate(moment(newDate).format('YYYY-MM-DD'));
  };
  const handleRouteDateChange = (newDate) => {
    setRouteDate(moment(newDate).format('YYYY-MM-DD'));
  };

  const fetchReaders = async (name) => {
    try {
      const response = await getReaderList();
      if (response.resultCode === 'Success') {
        setReaders(response.rows.filter((a) => a.county === name));
      }
    } catch (error) {
      console.error('Error fetching reader list:', error);
    }
  };

  const fetchRoutes = useCallback(async (name) => {
    try {
      const routeInfoData = await getRouteInfo(name);
      setRouteInfo(routeInfoData);
    } catch (error) {
      console.error('Error fetching routes:', error);
    }
  }, []);

  useEffect(() => {
    fetchReaders(teamName);
    fetchRoutes(teamName);
  }, [users.team_id]);

  // 新北2：載入攝影機清單
  useEffect(() => {
    if (teamName === 'newtaipei2') {
      getCctvListNewTaipei2()
        .then((data) => {
          const list = data.rows || [];
          setNt2Cameras(list);
          if (list.length > 0 && !nt2SelectedCamera) {
            setNt2SelectedCamera(list[0].camera_code || list[0].cameraCode || '');
          }
        })
        .catch((err) => console.error('載入新北2攝影機失敗:', err));
    }
  }, [teamName]);

  const TAB_STYLES = {
    fontSize: '0.9rem',
    fontWeight: 'medium',
    whiteSpace: 'nowrap'
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // 月報表下載（查詢歷史封存資料 vehicle_speed_records_archive）
  const handleHistoryReportDownload = async (type) => {
    try {
      const month = routeDate.slice(0, 7); // YYYY-MM
      const data = await getHistoryMonthlyHourlyRouteStats(selectroute, month, type);
      const { headers, data: csvData, month: dataMonth } = data;

      const BOM = '\uFEFF';
      let csvContent = `${BOM}${headers.join(',')}\n`;
      csvData.forEach((row) => {
        csvContent += `${row.join(',')}\n`;
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');

      const roadname = routeInfo.find((r) => r.id === selectroute)?.name || selectroute;
      const filenameSuffix = type === 'speed' ? '平均速度月統計' : '平均時間月統計';
      link.setAttribute('href', url);
      link.setAttribute('download', `${roadname}_${dataMonth}月${filenameSuffix}(歷史).csv`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下載歷史 CSV 失敗:', error);
    }
  };

  // 新北2月報下載
  const handleNt2MonthlyDownload = async () => {
    if (!nt2SelectedCamera) return;
    try {
      const month = nt2Date.slice(0, 7);
      const data = await getHistoryMonthlyDetectionStats(nt2SelectedCamera, month);
      const { headers, data: csvRows } = data;
      const BOM = '\uFEFF';
      let csvContent = `${BOM}${headers.join(',')}\n`;
      csvRows.forEach((row) => { csvContent += `${row.join(',')}\n`; });
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `新北2_${nt2SelectedCamera}_${month}_歷史車種偵測月統計.csv`);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('下載新北2歷史月報失敗:', err);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* ===== 新北2 專屬歷史資料 ===== */}
      {teamName === 'newtaipei2' ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper sx={{ height: '600px', p: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={0} aria-label="新北2歷史資料統計">
                  <Tab label={`歷史車種偵測統計 (${nt2Date})`} sx={TAB_STYLES} />
                </Tabs>
              </Box>
              <DailyVehicleDetectionChart
                date={nt2Date}
                handleDateChange={(newDate) => setNt2Date(moment(newDate).format('YYYY-MM-DD'))}
                teamName="newtaipei2"
                minDate={new Date('2020-01-01')}
              />
            </Paper>
          </Grid>

          {/* 已隱藏：新北2 歷史車種偵測月報表下載（保留程式碼，未刪除；恢復時移除外層 {false && (...)} 即可） */}
          {false && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                新北2 歷史車種偵測月報表下載
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <FormControl variant="standard" sx={{ minWidth: 160 }}>
                  <InputLabel>選擇攝影機</InputLabel>
                  <Select
                    value={nt2SelectedCamera}
                    onChange={(e) => setNt2SelectedCamera(e.target.value)}
                  >
                    {nt2Cameras.map((cam) => {
                      const code = cam.camera_code || cam.cameraCode || '';
                      return (
                        <MenuItem key={code} value={code}>
                          {code}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  月份：{nt2Date.slice(0, 7)}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!nt2SelectedCamera}
                  onClick={handleNt2MonthlyDownload}
                >
                  下載月報表 (CSV)
                </Button>
              </Box>
            </Paper>
          </Grid>
          )}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {/* 圖表區塊 */}
          <Grid item xs={12}>
            <Paper sx={{ height: '600px', p: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange} aria-label="歷史資料統計">
                  <Tab label={`歷史讀取器車流量統計 (${readerDate})`} sx={TAB_STYLES} />
                  <Tab label={`歷史路段旅行速度與時間統計 (${routeDate})`} sx={TAB_STYLES} />
                </Tabs>
              </Box>

              {tabValue === 0 && (
                <DailyReaderTrafficChart
                  readerData={readers}
                  date={readerDate}
                  handleDateChange={handleReaderDateChange}
                  dataFetcher={getDailyReaderHistoryStats}
                  minDate={HISTORY_MIN_DATE}
                />
              )}

              {tabValue === 1 && (
                <DailyRouteStatsChart
                  selectroute={selectroute}
                  setselectroute={setselectroute}
                  routeData={routeInfo}
                  date={routeDate}
                  handleDateChange={handleRouteDateChange}
                  dataFetcher={getHistoryRouteStats}
                  minDate={HISTORY_MIN_DATE}
                />
              )}
            </Paper>
          </Grid>

          {/* 歷史讀取器月報表下載 — 僅在讀取器 Tab 顯示 */}
          {tabValue === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  讀取器歷史資料查詢 — 月報表下載
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <FormControl variant="standard" sx={{ minWidth: 200 }}>
                    <InputLabel>選擇讀取器</InputLabel>
                    <Select
                      value={downloadReaderMac}
                      onChange={(e) => {
                        const r = readers.find((x) => x.leonReaderMACAddress === e.target.value);
                        setDownloadReaderMac(e.target.value);
                        setDownloadReaderName(r?.leonReaderName || e.target.value);
                      }}
                    >
                      {readers.map((r) => (
                        <MenuItem key={r.leonReaderMACAddress} value={r.leonReaderMACAddress}>
                          {r.leonReaderName || r.leonReaderMACAddress}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    月份：{readerDate.slice(0, 7)}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!downloadReaderMac}
                    onClick={async () => {
                      try {
                        const month = readerDate.slice(0, 7);
                        const data = await getHistoryMonthlyReaderStats(downloadReaderMac, month);
                        const { headers, data: csvRows } = data;
                        const BOM = '\uFEFF';
                        let csvContent = `${BOM}${headers.join(',')}\n`;
                        csvRows.forEach((row) => { csvContent += `${row.join(',')}\n`; });
                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.setAttribute('href', url);
                        link.setAttribute('download', `${downloadReaderName}_${month}_歷史車流月統計.csv`);
                        link.style.display = 'none';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        console.error('下載讀取器歷史月報失敗:', err);
                      }
                    }}
                  >
                    下載月報表 (CSV)
                  </Button>
                </Box>
              </Paper>
            </Grid>
          )}

          {/* 歷史路段月報表下載 — 僅在路段 Tab 顯示 */}
          {tabValue === 1 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  歷史旅行速度與旅行時間月報表下載
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleHistoryReportDownload('speed')}
                  >
                    下載歷史旅行速度月報表
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => handleHistoryReportDownload('time')}
                  >
                    下載歷史旅行時間月報表
                  </Button>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default HistoryDashboard;
